import { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Dialog,
  Textarea,
  Tag,
  Space,
  MessagePlugin,
  Select,
  Input,
} from 'tdesign-react';
import {
  CheckIcon,
  CloseIcon,
  SearchIcon,
} from 'tdesign-icons-react';
import { orderApi, dictApi } from '../../api';
import type { OrderFeeSettleBatchVO } from '../../types';

const statusMap: Record<number, { label: string; theme: string }> = {
  0: { label: '待审核', theme: 'warning' },
  1: { label: '审核通过', theme: 'success' },
  2: { label: '审核拒绝', theme: 'danger' },
};

const payerTypeMap: Record<number, string> = {
  1: '供应商',
  2: '采购商',
};

export default function FeePaymentManage() {
  const [data, setData] = useState<OrderFeeSettleBatchVO[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchStatus, setSearchStatus] = useState<number | undefined>(undefined);
  const [searchPayerType, setSearchPayerType] = useState<number | undefined>(undefined);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [auditDialogVisible, setAuditDialogVisible] = useState(false);
  const [auditingItem, setAuditingItem] = useState<OrderFeeSettleBatchVO | null>(null);
  const [auditRemark, setAuditRemark] = useState('');
  const [auditAction, setAuditAction] = useState<'pass' | 'reject'>('pass');
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [detailDialogVisible, setDetailDialogVisible] = useState(false);
  const [viewingItem, setViewingItem] = useState<OrderFeeSettleBatchVO | null>(null);

  // 服务费设置弹窗
  const [feeSettingVisible, setFeeSettingVisible] = useState(false);
  const [feeSettingLoading, setFeeSettingLoading] = useState(false);
  const [buyerFeeRate, setBuyerFeeRate] = useState('');
  const [supplierFeeRate, setSupplierFeeRate] = useState('');
  const [feeMode, setFeeMode] = useState<string>('1');

  const formatPrice = (price?: number) => {
    if (price === undefined || price === null) return '¥0.00';
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 2,
    }).format(price);
  };

  const statusOptions = [
    { label: '全部状态', value: undefined },
    { label: '待审核', value: 0 },
    { label: '审核通过', value: 1 },
    { label: '审核拒绝', value: 2 },
  ];

  const payerTypeOptions = [
    { label: '全部类型', value: undefined },
    { label: '供应商', value: 1 },
    { label: '采购商', value: 2 },
  ];

  const columns: any[] = [
    { colKey: 'id', title: 'ID', width: 80 },
    { colKey: 'settleNo', title: '结算单号', width: 180 },
    { colKey: 'payerCompanyName', title: '付款方', width: 160 },
    {
      colKey: 'payerType',
      title: '付款方类型',
      width: 120,
      cell: ({ row }: any) => payerTypeMap[row.payerType] || '-',
    },
    {
      colKey: 'orderCount',
      title: '订单数',
      width: 100,
      cell: ({ row }: any) => `${row.orderCount || 0} 个`,
    },
    {
      colKey: 'amount',
      title: '结算金额',
      width: 140,
      cell: ({ row }: any) => formatPrice(row.amount),
    },
    {
      colKey: 'paymentProof',
      title: '凭证图片',
      width: 100,
      cell: ({ row }: any) => {
        const paymentProof = row.paymentProof;
        if (!paymentProof || !Array.isArray(paymentProof) || paymentProof.length === 0) {
          return <Button theme="primary" variant="text" size="small" disabled>查看(0)</Button>;
        }
        return (
          <Button
            theme="primary"
            variant="text"
            size="small"
            onClick={() => handlePreviewImage(paymentProof[0])}
          >
            查看({paymentProof.length})
          </Button>
        );
      },
    },
    {
      colKey: 'status',
      title: '状态',
      width: 120,
      cell: ({ row }: any) => {
        const status = statusMap[row.status];
        return <Tag theme={status?.theme as any}>{status?.label || '未知'}</Tag>;
      },
    },
    { colKey: 'createTime', title: '提交时间', width: 180 },
    {
      colKey: 'action',
      title: '操作',
      width: 200,
      fixed: 'right',
      cell: ({ row }: any) => (
        <Space>
          <Button
            theme="primary"
            variant="text"
            icon={<SearchIcon />}
            onClick={() => handleViewDetail(row)}
          >
            详情
          </Button>
          {row.status === 0 && (
            <>
              <Button
                theme="success"
                variant="text"
                icon={<CheckIcon />}
                onClick={() => handleAudit(row, 'pass')}
              >
                通过
              </Button>
              <Button
                theme="danger"
                variant="text"
                icon={<CloseIcon />}
                onClick={() => handleAudit(row, 'reject')}
              >
                拒绝
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const fetchData = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const res = await orderApi.getFeePaymentPage({
        pageNum: page,
        pageSize,
        status: searchStatus,
        payerType: searchPayerType,
      });
      if (res.code === 200 && res.data) {
        const list = res.data.list || res.data.records || [];
        setData(list);
        setPagination({
          current: res.data.pageNum || page,
          pageSize: res.data.pageSize || pageSize,
          total: res.data.total || 0,
        });
      } else {
        MessagePlugin.error(res.message || '加载数据失败');
      }
    } catch (err) {
      MessagePlugin.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStatus, searchPayerType]);

  const handleAudit = (item: OrderFeeSettleBatchVO, action: 'pass' | 'reject') => {
    setAuditingItem(item);
    setAuditAction(action);
    setAuditRemark('');
    setAuditDialogVisible(true);
  };

  const handleViewDetail = (item: OrderFeeSettleBatchVO) => {
    setViewingItem(item);
    setDetailDialogVisible(true);
  };

  const handleAuditSubmit = async () => {
    if (!auditingItem) return;
    try {
      const res = await orderApi.auditFeePayment({
        paymentId: auditingItem.id,
        approved: auditAction === 'pass',
        remark: auditRemark,
      });
      if (res.code === 200) {
        MessagePlugin.success(auditAction === 'pass' ? '审核通过' : '审核拒绝');
        setAuditDialogVisible(false);
        fetchData();
      } else {
        MessagePlugin.error(res.message || '审核失败');
      }
    } catch (err) {
      MessagePlugin.error('审核失败');
    }
  };

  const formatSettleStatus = (status?: number) => {
    switch (status) {
      case 0: return '待审核';
      case 1: return '审核通过';
      case 2: return '审核拒绝';
      default: return '未知';
    }
  };

  const handlePreviewImage = (url: string) => {
    setPreviewImageUrl(url);
    setImagePreviewVisible(true);
  };

  // 打开服务费设置弹窗
  const openFeeSettingModal = async () => {
    setFeeSettingVisible(true);
    setFeeSettingLoading(true);
    try {
      const [buyerRes, supplierRes, modeRes] = await Promise.all([
        dictApi.getByKey('fee.buyer.rate').catch(() => null),
        dictApi.getByKey('fee.supplier.rate').catch(() => null),
        dictApi.getByKey('fee.mode').catch(() => null),
      ]);
      // 后端存的是小数比例（如 0.05 = 5%），前端展示为万分之整数
      const toWanfen = (val?: string) => {
        if (!val) return '';
        const n = parseFloat(val);
        return isNaN(n) ? '' : String(Math.round(n * 10000));
      };
      setBuyerFeeRate(toWanfen(buyerRes?.data?.dictValue));
      setSupplierFeeRate(toWanfen(supplierRes?.data?.dictValue));
      setFeeMode(modeRes?.data?.dictValue || '1');
    } catch {
      // ignore
    } finally {
      setFeeSettingLoading(false);
    }
  };

  // 提交服务费设置
  const handleFeeSettingSubmit = async () => {
    const buyer = parseFloat(buyerFeeRate);
    const supplier = parseFloat(supplierFeeRate);

    if (isNaN(buyer) || buyer < 0 || buyer > 10000) {
      MessagePlugin.error('采购商服务费比例需在 0~10000 之间（万分之）');
      return;
    }
    if (isNaN(supplier) || supplier < 0 || supplier > 10000) {
      MessagePlugin.error('供应商服务费比例需在 0~10000 之间（万分之）');
      return;
    }

    setFeeSettingLoading(true);
    try {
      // 前端输入为万分之整数，保存时转为小数比例；使用批量更新接口
      const res = await dictApi.updateBatch({
        'fee.buyer.rate': String(buyer / 10000) as any,
        'fee.supplier.rate': String(supplier / 10000),
        'fee.mode': feeMode,
      });
      if (res.code === 200) {
        MessagePlugin.success('服务费比例设置成功');
        setFeeSettingVisible(false);
      } else {
        MessagePlugin.error(res.message || '设置失败');
      }
    } catch (err: any) {
      MessagePlugin.error(err.message || '设置失败，请确认后端已配置 fee.buyer.rate、fee.supplier.rate、fee.mode 字典项');
    } finally {
      setFeeSettingLoading(false);
    }
  };

  return (
    <div className="min-w-0">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">服务费管理</h1>
      </div>

      {/* 搜索栏 */}
      <div className="bg-white rounded-lg p-4 mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">凭证状态：</span>
          <Select
            value={searchStatus}
            onChange={(val) => setSearchStatus(val as number | undefined)}
            options={statusOptions}
            placeholder="请选择状态"
            style={{ width: 160 }}
            clearable
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">付款方类型：</span>
          <Select
            value={searchPayerType}
            onChange={(val) => setSearchPayerType(val as number | undefined)}
            options={payerTypeOptions}
            placeholder="请选择类型"
            style={{ width: 160 }}
            clearable
          />
        </div>
        <Space>
          <Button theme="primary" onClick={() => fetchData(1, pagination.pageSize)}>
            查询
          </Button>
          <Button
            theme="default"
            variant="outline"
            onClick={() => {
              setSearchStatus(undefined);
              setSearchPayerType(undefined);
              fetchData(1, pagination.pageSize);
            }}
          >
            重置
          </Button>
          <Button
            theme="warning"
            variant="outline"
            onClick={() => openFeeSettingModal()}
          >
            服务费设置
          </Button>
        </Space>
      </div>

      <Table
        data={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          onChange: (pageInfo: any) => fetchData(pageInfo.current, pageInfo.pageSize),
        }}
      />

      {/* 审核对话框 */}
      <Dialog
        header={auditAction === 'pass' ? '审核通过' : '审核拒绝'}
        visible={auditDialogVisible}
        onClose={() => setAuditDialogVisible(false)}
        onConfirm={handleAuditSubmit}
        width={600}
      >
        <div className="py-4 space-y-4">
          {auditingItem?.paymentProof && auditingItem.paymentProof.length > 0 && (
            <div>
              <div className="mb-2 font-medium">转款凭证：</div>
              <div className="flex flex-wrap gap-2">
                {auditingItem.paymentProof.map((url, idx) => (
                  <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt={`凭证-${idx}`} className="w-20 h-20 rounded object-cover border border-gray-200 hover:border-[#1c5bff] transition-colors" />
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">批次ID</span>
              <span className="text-gray-700">{auditingItem?.id}</span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">结算单号</span>
              <span className="text-gray-700">{auditingItem?.settleNo || '-'}</span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">付款方</span>
              <span className="text-gray-700">{`${payerTypeMap[auditingItem?.payerType || 0] || '-'} · ${auditingItem?.payerCompanyName || '-'}`}</span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">订单数量</span>
              <span className="text-gray-700">{`${auditingItem?.orderCount || 0} 个`}</span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">结算金额</span>
              <span className="text-gray-700 font-medium">{formatPrice(auditingItem?.amount)}</span>
            </div>
            {auditingItem?.payerRemark && (
              <div className="flex">
                <span className="text-gray-400 w-20 shrink-0">付款备注</span>
                <span className="text-gray-700">{auditingItem.payerRemark}</span>
              </div>
            )}
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">状态</span>
              <span>
                {auditingItem?.status !== undefined ? (
                  <Tag size="small" theme={statusMap[auditingItem.status]?.theme as any}>
                    {statusMap[auditingItem.status]?.label}
                  </Tag>
                ) : '-'}
              </span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">提交时间</span>
              <span className="text-gray-700">{auditingItem?.createTime || '-'}</span>
            </div>
          </div>
          <div>
            <div className="mb-2 font-medium">审核备注：</div>
            <Textarea
              value={auditRemark}
              onChange={(value) => setAuditRemark(value)}
              placeholder="请输入审核备注（可选）"
              rows={3}
            />
          </div>
        </div>
      </Dialog>

      {/* 详情对话框 */}
      <Dialog
        header="结算批次详情"
        visible={detailDialogVisible}
        onClose={() => setDetailDialogVisible(false)}
        confirmBtn={null}
        width={700}
      >
        <div className="py-4 space-y-4">
          {viewingItem?.paymentProof && viewingItem.paymentProof.length > 0 && (
            <div>
              <div className="mb-2 font-medium">转款凭证：</div>
              <div className="flex flex-wrap gap-2">
                {viewingItem.paymentProof.map((url, idx) => (
                  <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt={`凭证-${idx}`} className="w-20 h-20 rounded object-cover border border-gray-200 hover:border-[#1c5bff] transition-colors" />
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">批次ID</span>
              <span className="text-gray-700">{viewingItem?.id}</span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">结算单号</span>
              <span className="text-gray-700">{viewingItem?.settleNo || '-'}</span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">付款方</span>
              <span className="text-gray-700">{`${payerTypeMap[viewingItem?.payerType || 0] || '-'} · ${viewingItem?.payerCompanyName || '-'}`}</span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">订单数量</span>
              <span className="text-gray-700">{`${viewingItem?.orderCount || 0} 个`}</span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">结算金额</span>
              <span className="text-gray-700 font-medium">{formatPrice(viewingItem?.amount)}</span>
            </div>
            {viewingItem?.payerRemark && (
              <div className="flex">
                <span className="text-gray-400 w-20 shrink-0">付款备注</span>
                <span className="text-gray-700">{viewingItem.payerRemark}</span>
              </div>
            )}
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">状态</span>
              <span>
                {viewingItem?.status !== undefined ? (
                  <Tag size="small" theme={statusMap[viewingItem.status]?.theme as any}>
                    {statusMap[viewingItem.status]?.label}
                  </Tag>
                ) : '-'}
              </span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-20 shrink-0">提交时间</span>
              <span className="text-gray-700">{viewingItem?.createTime || '-'}</span>
            </div>
            {viewingItem?.auditTime && (
              <div className="flex">
                <span className="text-gray-400 w-20 shrink-0">审核时间</span>
                <span className="text-gray-700">{viewingItem.auditTime}</span>
              </div>
            )}
            {viewingItem?.auditRemark && (
              <div className="flex col-span-2">
                <span className="text-gray-400 w-20 shrink-0">审核备注</span>
                <span className={viewingItem.status === 2 ? 'text-red-600' : 'text-gray-700'}>{viewingItem.auditRemark}</span>
              </div>
            )}
          </div>
          {viewingItem?.orders && viewingItem.orders.length > 0 && (
            <div>
              <div className="font-medium mb-2">包含订单</div>
              <Table
                data={viewingItem.orders}
                columns={[
                  { colKey: 'orderNo', title: '订单编号' },
                  { colKey: 'buyerCompanyName', title: '采购商' },
                  { colKey: 'supplierCompanyName', title: '供应商' },
                  { colKey: 'totalAmount', title: '订单金额', cell: ({ row }: any) => formatPrice(row.totalAmount) },
                ]}
                rowKey="id"
                size="small"
                bordered
              />
            </div>
          )}
        </div>
      </Dialog>

      {/* 图片预览对话框 */}
      <Dialog
        header="凭证预览"
        visible={imagePreviewVisible}
        onClose={() => setImagePreviewVisible(false)}
        footer={null}
        width={700}
      >
        <div className="py-4 flex justify-center">
          <img
            src={previewImageUrl}
            alt="转款凭证"
            className="max-h-[60vh] rounded object-contain"
          />
        </div>
      </Dialog>

      {/* 服务费设置弹窗 */}
      <Dialog
        header="服务费比例设置"
        visible={feeSettingVisible}
        onClose={() => setFeeSettingVisible(false)}
        onConfirm={handleFeeSettingSubmit}
        width={520}
      >
        <div className="py-3 space-y-4">
          {feeSettingLoading && (
            <div className="text-sm text-gray-400">加载中...</div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">服务费收取模式</label>
              <Select
                value={feeMode}
                onChange={(val) => setFeeMode(String(val))}
                options={[
                  { label: '1-仅向供应商收取', value: '1' },
                  { label: '2-仅向采购商收取', value: '2' },
                  { label: '3-双向收取', value: '3' },
                ]}
                style={{ width: '100%' }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">采购商服务费比例（万分之）</label>
                <Input
                  value={buyerFeeRate}
                  onChange={(val) => setBuyerFeeRate(val)}
                  placeholder="如 500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">供应商服务费比例（万分之）</label>
                <Input
                  value={supplierFeeRate}
                  onChange={(val) => setSupplierFeeRate(val)}
                  placeholder="如 500"
                />
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
            <p>提示：单位为万分之。例如 500 表示 5%（500/10000）。设置后，新创建的订单将按此比例计算服务费。</p>
            <p className="mt-1 text-amber-600">对应字典项：fee.buyer.rate、fee.supplier.rate、fee.mode。</p>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
